import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
    Lock,
    Upload,
    Copy,
    Trash2,
    FileText,
    Image as ImageIcon,
    Film,
    File as FileIcon,
    ArrowLeft,
    Check,
    Loader2,
    Star,
    Paperclip,
    ChevronDown,
    Download,
    RefreshCw,
    KeyRound,
    Eye,
    EyeOff,
    Newspaper,
    LayoutGrid,
    Home,
} from 'lucide-react';
import { caseStudies } from '@/data/caseStudies';
import { humanSize } from '@/lib/format';
import { projects as ALL_CASE_STUDIES } from '@/components/portfolio/Projects';
import StoriesManager from '@/components/admin/StoriesManager';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_KEY = 'admin_passcode';
const CASE_STUDY_SLUGS = Object.keys(caseStudies);

// ────────────────────────────────────────────────────────────
// Passcode gate
// ────────────────────────────────────────────────────────────
const Gate = ({ onUnlock }) => {
    const [code, setCode] = useState('');
    const [err, setErr] = useState('');
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setErr('');
        setBusy(true);
        try {
            const r = await fetch(`${API}/admin/verify`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ passcode: code }),
            });
            if (!r.ok) {
                setErr('Incorrect passcode');
                setBusy(false);
                return;
            }
            localStorage.setItem(LS_KEY, code);
            onUnlock(code);
        } catch (e2) {
            setErr(e2.message || 'Something went wrong');
            setBusy(false);
        }
    };

    return (
        <section className="relative min-h-[100svh] flex items-center justify-center px-4 py-16">
            <div className="mx-auto max-w-md w-full">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
                    className="rounded-[22px] border border-[var(--line)] bg-[var(--surface)] p-8 md:p-10 relative overflow-hidden"
                >
                    <div
                        aria-hidden
                        className="pointer-events-none absolute inset-0 opacity-[0.3]"
                        style={{
                            backgroundImage:
                                'radial-gradient(circle, color-mix(in oklab, var(--ink-3) 45%, transparent) 1px, transparent 1px)',
                            backgroundSize: '22px 22px',
                        }}
                    />

                    <div className="relative">
                        <div className="inline-flex items-center gap-3 font-mono-brand text-[10.5px] uppercase tracking-[0.24em] text-[var(--ink-3)]">
                            <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent)] pulse-dot" />
                            <span>Admin</span>
                        </div>

                        <h1 className="mt-6 font-display leading-[0.95] text-4xl md:text-5xl text-[var(--ink)]">
                            <span className="font-display-italic text-[var(--accent)]">
                                Locked
                            </span>
                            <span className="text-[var(--ink-2)]">.</span>
                        </h1>

                        <p className="mt-3 font-sans-brand text-[13.5px] text-[var(--ink-2)] leading-[1.6]">
                            Enter the passcode to manage uploads and public
                            file links.
                        </p>

                        <form onSubmit={submit} className="mt-8 flex flex-col gap-3">
                            <label className="relative">
                                <span className="sr-only">Passcode</span>
                                <input
                                    type="password"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value)}
                                    placeholder="••••••••"
                                    autoFocus
                                    minLength={8}
                                    data-testid="admin-passcode-input"
                                    className="w-full pl-11 pr-4 py-3.5 rounded-full border border-[var(--line-2)] bg-[var(--surface-2)] text-[var(--ink)] font-mono-brand tracking-[0.4em] text-lg placeholder:text-[var(--ink-3)] outline-none focus:outline-none focus-visible:outline-none focus:border-[var(--accent)] transition-colors"
                                />
                                <Lock
                                    size={14}
                                    className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--ink-3)]"
                                />
                            </label>

                            {err && (
                                <div
                                    data-testid="admin-passcode-error"
                                    className="text-sm text-[var(--warn)] font-mono-brand"
                                >
                                    {err}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={busy || !code}
                                data-testid="admin-passcode-submit"
                                data-cursor="hover"
                                className="mt-2 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[var(--accent)] text-[var(--accent-ink)] text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity"
                            >
                                {busy ? (
                                    <Loader2 size={14} className="animate-spin" />
                                ) : (
                                    'Unlock'
                                )}
                            </button>
                        </form>

                        <div className="mt-6 flex items-center justify-between text-[11px] font-mono-brand uppercase tracking-[0.22em] text-[var(--ink-3)]">
                            <Link
                                to="/"
                                data-cursor="hover"
                                className="inline-flex items-center gap-1.5 hover:text-[var(--ink)] transition-colors"
                            >
                                <ArrowLeft size={11} /> Back home
                            </Link>
                            <span>Private area</span>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

// ────────────────────────────────────────────────────────────
// File-type helpers
// ────────────────────────────────────────────────────────────
const iconFor = (ct) => {
    if (!ct) return FileIcon;
    if (ct.startsWith('image/')) return ImageIcon;
    if (ct.startsWith('video/')) return Film;
    if (ct === 'application/pdf' || ct.startsWith('text/')) return FileText;
    return FileIcon;
};

const shortName = (n, max = 36) =>
    n && n.length > max ? `${n.slice(0, max - 3)}...` : n;

// ────────────────────────────────────────────────────────────
// File card
// ────────────────────────────────────────────────────────────
const FileCard = ({ file, onDelete, onToggleResume, onAttach }) => {
    const [copied, setCopied] = useState(false);
    const [attachOpen, setAttachOpen] = useState(false);
    const Icon = iconFor(file.content_type);
    const isImage = file.content_type?.startsWith('image/');
    const publicUrl = `${process.env.REACT_APP_BACKEND_URL}/api/files/${file.id}`;

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(publicUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            /* clipboard not available, noop */
        }
    };

    const csTitle = file.case_study_slug
        ? caseStudies[file.case_study_slug]?.title || file.case_study_slug
        : null;

    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.2, 0.8, 0.2, 1] }}
            data-testid={`file-card-${file.id}`}
            data-is-resume={file.is_active_resume ? 'true' : 'false'}
            className={`group relative flex flex-col rounded-xl border overflow-hidden transition-colors ${
                file.is_active_resume
                    ? 'border-[var(--accent)] bg-[color-mix(in_oklab,var(--accent)_6%,var(--surface))]'
                    : 'border-[var(--line)] bg-[var(--surface)] hover:border-[var(--line-2)]'
            }`}
        >
            {/* Preview (square, dense) */}
            <div className="relative aspect-square w-full bg-[var(--surface-2)] overflow-hidden">
                {isImage ? (
                    <img
                        src={publicUrl}
                        alt={file.original_filename}
                        className="absolute inset-0 w-full h-full object-cover"
                        loading="lazy"
                    />
                ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 text-[var(--ink-3)]">
                        <Icon size={30} />
                        <span className="font-mono-brand text-[9px] uppercase tracking-[0.22em]">
                            {file.content_type?.split('/')[1] || 'file'}
                        </span>
                    </div>
                )}

                <span className="absolute top-1.5 left-1.5 rounded-full bg-black/55 text-white border border-white/15 backdrop-blur-md px-2 py-0.5 font-mono-brand text-[9px] tabular-nums">
                    {humanSize(file.size)}
                </span>

                {/* Top-right badges (compact icons) */}
                <div className="absolute top-1.5 right-1.5 flex items-center gap-1">
                    {file.is_active_resume && (
                        <span
                            data-testid={`badge-resume-${file.id}`}
                            title="Active resume"
                            className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[var(--accent)] text-[var(--accent-ink)]"
                        >
                            <Star size={10} fill="currentColor" />
                        </span>
                    )}
                    {csTitle && (
                        <span
                            data-testid={`badge-attached-${file.id}`}
                            className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-black/60 text-[var(--accent)] border border-white/15 backdrop-blur-md"
                            title={`Attached to: ${csTitle}`}
                        >
                            <Paperclip size={10} />
                        </span>
                    )}
                </div>

                {/* Hover overlay: primary actions */}
                <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/55 backdrop-blur-[2px] opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <button
                        type="button"
                        onClick={copy}
                        data-testid={`copy-url-${file.id}`}
                        data-cursor="hover"
                        title="Copy public URL"
                        className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/10 border border-white/25 text-white hover:bg-[var(--accent)] hover:text-[var(--accent-ink)] hover:border-[var(--accent)] transition-colors"
                    >
                        {copied ? <Check size={15} /> : <Copy size={15} />}
                    </button>
                    <a
                        href={publicUrl}
                        target="_blank"
                        rel="noreferrer"
                        data-cursor="hover"
                        data-testid={`open-${file.id}`}
                        title="Open in new tab"
                        className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/10 border border-white/25 text-white hover:bg-[var(--accent)] hover:text-[var(--accent-ink)] hover:border-[var(--accent)] transition-colors"
                    >
                        <Icon size={15} />
                    </a>
                    <button
                        type="button"
                        onClick={() => onDelete(file.id)}
                        data-testid={`delete-${file.id}`}
                        data-cursor="hover"
                        aria-label="Delete file"
                        title="Delete"
                        className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/10 border border-white/25 text-white hover:bg-[var(--warn)] hover:text-white hover:border-[var(--warn)] transition-colors"
                    >
                        <Trash2 size={15} />
                    </button>
                </div>
            </div>

            {/* Footer: name + inline controls */}
            <div className="flex items-center gap-1.5 px-2.5 py-2">
                <div className="min-w-0 flex-1">
                    <div
                        className="font-sans-brand text-[11.5px] text-[var(--ink)] leading-tight truncate"
                        title={file.original_filename}
                    >
                        {shortName(file.original_filename, 22)}
                    </div>
                </div>

                <button
                    type="button"
                    onClick={() => onToggleResume(file.id)}
                    data-testid={`toggle-resume-${file.id}`}
                    data-cursor="hover"
                    title={
                        file.is_active_resume
                            ? 'Unset as active resume'
                            : 'Set as active resume'
                    }
                    className={`inline-flex items-center justify-center w-7 h-7 rounded-full border shrink-0 transition-colors ${
                        file.is_active_resume
                            ? 'bg-[var(--accent)] text-[var(--accent-ink)] border-[var(--accent)]'
                            : 'border-[var(--line-2)] bg-[var(--surface-2)] text-[var(--ink-3)] hover:text-[var(--accent)] hover:border-[var(--accent)]'
                    }`}
                >
                    <Star
                        size={12}
                        fill={file.is_active_resume ? 'currentColor' : 'none'}
                    />
                </button>

                <div className="relative shrink-0">
                    <button
                        type="button"
                        onClick={() => setAttachOpen((v) => !v)}
                        data-testid={`attach-toggle-${file.id}`}
                        data-cursor="hover"
                        aria-expanded={attachOpen}
                        title={csTitle ? `Attached · ${csTitle}` : 'Attach to case study'}
                        className={`inline-flex items-center justify-center w-7 h-7 rounded-full border transition-colors ${
                            csTitle
                                ? 'border-[var(--accent)] text-[var(--accent)] bg-[color-mix(in_oklab,var(--accent)_8%,transparent)]'
                                : 'border-[var(--line-2)] bg-[var(--surface-2)] text-[var(--ink-3)] hover:text-[var(--ink)] hover:border-[var(--accent)]'
                        }`}
                    >
                        <Paperclip size={12} />
                    </button>
                    <AnimatePresence>
                        {attachOpen && (
                            <motion.div
                                initial={{ opacity: 0, y: -6 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -6 }}
                                transition={{ duration: 0.2 }}
                                className="absolute z-20 right-0 bottom-full mb-1 w-56 rounded-2xl border border-[var(--line-2)] bg-[var(--surface)] shadow-xl overflow-hidden"
                            >
                                <button
                                    type="button"
                                    data-testid={`attach-none-${file.id}`}
                                    onClick={() => {
                                        onAttach(file.id, null);
                                        setAttachOpen(false);
                                    }}
                                    className="w-full text-left px-3 py-2 text-[11.5px] font-mono-brand uppercase tracking-[0.18em] text-[var(--ink-3)] hover:bg-[var(--surface-2)] hover:text-[var(--warn)] transition-colors border-b border-[var(--line)]"
                                >
                                    · None (detach)
                                </button>
                                <div className="max-h-56 overflow-y-auto">
                                    {CASE_STUDY_SLUGS.map((s) => (
                                        <button
                                            key={s}
                                            type="button"
                                            data-testid={`attach-${s}-${file.id}`}
                                            onClick={() => {
                                                onAttach(file.id, s);
                                                setAttachOpen(false);
                                            }}
                                            className={`w-full text-left px-3 py-2 text-[12px] hover:bg-[var(--surface-2)] transition-colors flex items-center justify-between ${
                                                file.case_study_slug === s
                                                    ? 'text-[var(--accent)]'
                                                    : 'text-[var(--ink)]'
                                            }`}
                                        >
                                            <span className="truncate">
                                                {caseStudies[s].title}
                                            </span>
                                            {file.case_study_slug === s && (
                                                <Check size={12} />
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </motion.div>
    );
};

// ────────────────────────────────────────────────────────────
// Uploader
// ────────────────────────────────────────────────────────────
const Uploader = ({ passcode, onUploaded }) => {
    const inputRef = useRef(null);
    const [dragging, setDragging] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const handleFiles = async (files) => {
        if (!files || files.length === 0) return;
        setError('');
        setBusy(true);

        for (const file of Array.from(files)) {
            const form = new FormData();
            form.append('file', file);

            try {
                const r = await fetch(`${API}/admin/upload`, {
                    method: 'POST',
                    headers: { 'X-Admin-Passcode': passcode },
                    body: form,
                });
                if (!r.ok) {
                    const detail = await r.text();
                    throw new Error(detail || `Upload failed (${r.status})`);
                }
                const rec = await r.json();
                onUploaded(rec);
            } catch (e) {
                setError(e.message || 'Upload failed');
            }
        }

        setBusy(false);
        if (inputRef.current) inputRef.current.value = '';
    };

    return (
        <div
            data-testid="admin-uploader"
            onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                handleFiles(e.dataTransfer.files);
            }}
            className={`relative rounded-[22px] border-2 border-dashed p-6 md:p-8 flex flex-col items-center justify-center gap-3 text-center transition-colors h-full ${
                dragging
                    ? 'border-[var(--accent)] bg-[color-mix(in_oklab,var(--accent)_10%,transparent)]'
                    : 'border-[var(--line-2)] bg-[var(--surface)]'
            }`}
        >
            <input
                ref={inputRef}
                type="file"
                multiple
                onChange={(e) => handleFiles(e.target.files)}
                className="hidden"
                data-testid="admin-file-input"
            />

            <span className="inline-flex items-center justify-center w-14 h-14 rounded-full border border-[var(--line-2)] bg-[var(--surface-2)] text-[var(--accent)]">
                {busy ? (
                    <Loader2 size={22} className="animate-spin" />
                ) : (
                    <Upload size={22} />
                )}
            </span>

            <div>
                <div className="font-display leading-[1] text-2xl md:text-3xl text-[var(--ink)]">
                    Drop files{' '}
                    <span className="font-display-italic text-[var(--accent)]">
                        here
                    </span>
                    <span className="text-[var(--ink-2)]">.</span>
                </div>
                <div className="mt-1.5 font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[var(--ink-3)]">
                    Images · PDFs · Video · Anything
                </div>
            </div>

            <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={busy}
                data-testid="admin-browse-button"
                data-cursor="hover"
                className="inline-flex items-center gap-2 pl-5 pr-2 py-2 rounded-full bg-[var(--accent)] text-[var(--accent-ink)] text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity"
            >
                {busy ? 'Uploading…' : 'Browse files'}
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[var(--accent-ink)] text-[var(--accent)]">
                    <Upload size={13} />
                </span>
            </button>

            {error && (
                <div
                    data-testid="admin-uploader-error"
                    className="text-sm text-[var(--warn)] font-mono-brand"
                >
                    {error}
                </div>
            )}
        </div>
    );
};

// ────────────────────────────────────────────────────────────
// Resume manager, dedicated CMS panel for the active CV
// ────────────────────────────────────────────────────────────
// Shows the current resume (fetched from the same /api/resume endpoint the
// public ResumeCard uses) with size + upload date, a download button, and a
// prominent "Replace Resume" action that uploads a new PDF and atomically
// marks it as the active resume so all Download-CV surfaces update.
const ResumeManager = ({ passcode, files, loading, onReplaced, onReload }) => {
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const inputRef = useRef(null);

    const active = files.find((f) => f.is_active_resume);
    const publicUrl = active
        ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${active.id}`
        : null;

    const upload = async (fileList) => {
        if (!fileList || fileList.length === 0) return;
        const file = fileList[0];
        // Sanity: only accept PDFs for the resume slot
        if (file.type && file.type !== 'application/pdf') {
            setErr('Resume must be a PDF file');
            return;
        }
        setErr('');
        setBusy(true);
        try {
            // 1) Upload
            const form = new FormData();
            form.append('file', file);
            const up = await fetch(`${API}/admin/upload`, {
                method: 'POST',
                headers: { 'X-Admin-Passcode': passcode },
                body: form,
            });
            if (!up.ok) throw new Error(await up.text());
            const rec = await up.json();

            // 2) Mark as active resume
            const flag = await fetch(
                `${API}/admin/files/${rec.id}/set-resume`,
                { method: 'POST', headers: { 'X-Admin-Passcode': passcode } }
            );
            if (!flag.ok) throw new Error('Could not mark as active resume');

            // 3) Optimistic update for immediate UI feedback
            onReplaced({ ...rec, is_active_resume: true });
            // 4) Authoritative reload so a late StrictMode fetch cannot
            //    clobber the new state (root cause of the stale-UI race).
            if (onReload) await onReload();
        } catch (e) {
            setErr(e.message || 'Upload failed');
        } finally {
            setBusy(false);
            if (inputRef.current) inputRef.current.value = '';
        }
    };

    return (
        <div
            data-testid="admin-resume-manager"
            className="rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-5 md:p-6 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6"
        >
            <div className="flex items-center gap-3 md:gap-4 flex-1 min-w-0">
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-[color-mix(in_oklab,var(--accent)_16%,var(--surface-2))] flex items-center justify-center text-[var(--accent)] shrink-0">
                    <FileText size={22} />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-[var(--ink-3)]">
                        Active CV · publicly downloadable
                    </div>
                    <div
                        className="mt-1 font-display text-xl md:text-2xl text-[var(--ink)] truncate"
                        data-testid="admin-resume-name"
                    >
                        {loading
                            ? 'Loading resume…'
                            : active
                                ? active.original_filename
                                : 'No resume set'}
                    </div>
                    <div className="mt-1 font-sans-brand text-[12.5px] text-[var(--ink-2)]">
                        {loading ? (
                            <>Fetching current CV state.</>
                        ) : active ? (
                            <>
                                PDF · {humanSize(active.size)} · updated{' '}
                                {new Date(active.created_at).toLocaleDateString()}
                            </>
                        ) : (
                            'Upload a PDF to power the Download-CV button portfolio-wide.'
                        )}
                    </div>
                </div>
            </div>

            <div className="flex items-center gap-2 w-full md:w-auto">
                {publicUrl && (
                    <a
                        href={publicUrl}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="admin-resume-download"
                        data-cursor="hover"
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-[var(--line-2)] bg-[var(--surface-2)] text-[var(--ink)] text-[13px] font-medium hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors"
                        title="Open in new tab"
                    >
                        <Download size={13} />
                        Download
                    </a>
                )}
                <input
                    ref={inputRef}
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    data-testid="admin-resume-file-input"
                    onChange={(e) => upload(e.target.files)}
                />
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    disabled={busy || loading}
                    data-testid="admin-resume-replace"
                    data-cursor="hover"
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[var(--accent)] text-[var(--accent-ink)] text-[13px] font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity"
                >
                    {busy || loading ? (
                        <Loader2 size={13} className="animate-spin" />
                    ) : active ? (
                        <RefreshCw size={13} />
                    ) : (
                        <Upload size={13} />
                    )}
                    {loading
                        ? 'Loading…'
                        : busy
                            ? 'Uploading…'
                            : active
                                ? 'Replace resume'
                                : 'Upload resume'}
                </button>
            </div>

            {err && (
                <div
                    data-testid="admin-resume-error"
                    className="w-full md:w-auto text-[12px] text-[var(--warn)] font-mono-brand"
                >
                    {err}
                </div>
            )}
        </div>
    );
};

// ────────────────────────────────────────────────────────────
// Case Studies Manager, visibility toggles for the public portfolio
// ────────────────────────────────────────────────────────────
// Every case study defined in components/portfolio/Projects.jsx `projects`
// list is surfaced here as a card. Flipping the toggle POSTs to
//   /api/admin/case-studies/{slug}/visibility  { visible: bool }
// and any public surface that consumes useCaseStudyVisibility() (Projects
// carousel, WorkIndex grid, Hero spotlight) reflects the change on next
// mount / window focus.
const CaseStudyManager = ({ passcode }) => {
    const [visibility, setVisibility] = useState({}); // slug → bool
    const [busy, setBusy] = useState({}); // slug → bool
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState('');

    const load = async () => {
        setErr('');
        try {
            const r = await fetch(`${API}/case-studies/visibility`, {
                cache: 'no-store',
            });
            if (!r.ok) throw new Error(`Load failed (${r.status})`);
            const rows = await r.json();
            const map = {};
            for (const row of rows) {
                if (row && row.slug) map[row.slug] = row.visible !== false;
            }
            setVisibility(map);
        } catch (e) {
            setErr(e.message || 'Failed to load visibility');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const toggle = async (slug, next) => {
        setBusy((b) => ({ ...b, [slug]: true }));
        try {
            const r = await fetch(
                `${API}/admin/case-studies/${slug}/visibility`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Admin-Passcode': passcode,
                    },
                    body: JSON.stringify({ visible: next }),
                }
            );
            if (!r.ok) throw new Error(`Toggle failed (${r.status})`);
            setVisibility((v) => ({ ...v, [slug]: next }));
        } catch (e) {
            setErr(e.message || 'Toggle failed');
        } finally {
            setBusy((b) => ({ ...b, [slug]: false }));
        }
    };

    // Undefined → treat as "unknown" while `loading`; once `loading`
    // resolves, missing entries default to visible=true (backend never
    // stores default-true records). This avoids the pre-hydration flash
    // where all 7 cards render as ON before the fetch resolves.
    const isOn = (slug) => {
        if (visibility[slug] !== undefined) return visibility[slug];
        return !loading; // during loading, don't claim visibility yet
    };

    const visibleCount = loading
        ? 0
        : ALL_CASE_STUDIES.filter((p) => isOn(p.slug)).length;

    return (
        <div
            data-testid="admin-case-studies-manager"
            className="rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-5 md:p-6"
        >
            <div className="flex items-end justify-between gap-4 flex-wrap mb-4">
                <div>
                    <div className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-[var(--ink-3)]">
                        Case studies · publicly listed
                    </div>
                    <h2 className="mt-1 font-display text-xl md:text-2xl text-[var(--ink)]">
                        Visibility{' '}
                        <span className="font-display-italic text-[var(--accent)]">
                            manager
                        </span>
                        <span className="text-[var(--ink-2)]">.</span>
                    </h2>
                </div>
                <div className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-[var(--ink-3)] tabular-nums">
                    {String(visibleCount).padStart(2, '0')} of{' '}
                    {String(ALL_CASE_STUDIES.length).padStart(2, '0')} live
                </div>
            </div>

            {err && (
                <div
                    data-testid="admin-case-studies-error"
                    className="mb-4 text-[12px] font-mono-brand text-[var(--warn)]"
                >
                    {err}
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                {ALL_CASE_STUDIES.map((p) => {
                    const on = isOn(p.slug);
                    const b = busy[p.slug];
                    return (
                        <div
                            key={p.slug}
                            data-testid={`admin-cs-card-${p.slug}`}
                            data-visible={on ? 'true' : 'false'}
                            className={`relative rounded-xl border ${
                                on
                                    ? 'border-[var(--line-2)]'
                                    : 'border-dashed border-[var(--line)] opacity-70'
                            } bg-[var(--surface-2)] px-4 py-3 flex items-center gap-3 transition-opacity`}
                        >
                            <div className="min-w-0 flex-1">
                                <div className="font-mono-brand text-[9.5px] uppercase tracking-[0.24em] text-[var(--ink-3)] tabular-nums">
                                    {p.no} · {p.category} ·{' '}
                                    <span
                                        className={
                                            on
                                                ? 'text-[var(--accent)]'
                                                : 'text-[var(--ink-3)]'
                                        }
                                    >
                                        {loading ? 'Loading…' : on ? 'Live' : 'Hidden'}
                                    </span>
                                </div>
                                <div className="mt-0.5 font-display text-[15px] text-[var(--ink)] leading-tight truncate">
                                    {p.title}
                                </div>
                            </div>
                            <button
                                type="button"
                                role="switch"
                                aria-checked={on}
                                disabled={b || loading}
                                onClick={() => toggle(p.slug, !on)}
                                data-testid={`admin-cs-toggle-${p.slug}`}
                                data-cursor="hover"
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 ${
                                    on
                                        ? 'bg-[var(--accent)]'
                                        : 'bg-[var(--line-2)]'
                                } disabled:opacity-60`}
                            >
                                <span
                                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${
                                        on
                                            ? 'translate-x-[22px]'
                                            : 'translate-x-[2px]'
                                    }`}
                                />
                                {b && (
                                    <Loader2
                                        size={10}
                                        className="absolute right-1 top-1/2 -translate-y-1/2 animate-spin text-[var(--accent-ink)]"
                                    />
                                )}
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ────────────────────────────────────────────────────────────
// Passcode manager, lets the user rotate the admin passcode
// from the UI. Backend enforces min-length (8), a wrong current
// passcode, and same-as-current rejection; we surface each error
// verbatim so the reader always knows what failed. Also shows the
// last-rotated timestamp fetched from `/api/admin/passcode-meta`.
// ────────────────────────────────────────────────────────────
const MIN_PASSCODE_LEN = 8;

const formatRotatedDate = (iso) => {
    if (!iso) return null;
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.valueOf())) return null;
        return d.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    } catch {
        return null;
    }
};

const PasscodeManager = ({ passcode, onRotated, defaultOpen = false }) => {
    const [open, setOpen] = useState(defaultOpen);
    const [current, setCurrent] = useState('');
    const [next, setNext] = useState('');
    const [confirm, setConfirm] = useState('');
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNext, setShowNext] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const [ok, setOk] = useState(false);
    const [meta, setMeta] = useState({ updated_at: null });

    // Fetch the last-rotated date whenever the passcode changes (so a fresh
    // rotation reflects immediately).
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const r = await fetch(`${API}/admin/passcode-meta`, {
                    headers: { 'X-Admin-Passcode': passcode },
                });
                if (r.ok && alive) setMeta(await r.json());
            } catch {
                /* silent, meta is optional */
            }
        })();
        return () => {
            alive = false;
        };
    }, [passcode]);

    const reset = () => {
        setCurrent('');
        setNext('');
        setConfirm('');
        setErr('');
        setOk(false);
        setShowCurrent(false);
        setShowNext(false);
    };

    const onToggle = () => {
        if (open) reset();
        setOpen((v) => !v);
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setErr('');
        setOk(false);
        if (next.length < MIN_PASSCODE_LEN) {
            setErr(`New passcode must be at least ${MIN_PASSCODE_LEN} characters`);
            return;
        }
        if (next !== confirm) {
            setErr('The two new-passcode fields do not match');
            return;
        }
        setBusy(true);
        try {
            const r = await fetch(`${API}/admin/change-passcode`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ current, next }),
            });
            if (r.status === 429) {
                setErr('Too many attempts, please wait a minute and try again.');
                return;
            }
            const data = await r.json().catch(() => ({}));
            if (!r.ok) {
                setErr(data.detail || `Change failed (${r.status})`);
                return;
            }
            setOk(true);
            // Persist the new passcode locally so subsequent admin calls keep
            // working without the user having to sign in again.
            localStorage.setItem(LS_KEY, next);
            // Reflect the fresh rotation date immediately
            setMeta({ updated_at: new Date().toISOString() });
            onRotated?.(next);
            setTimeout(() => {
                setOpen(false);
                reset();
            }, 1400);
        } catch (e2) {
            setErr(e2.message || 'Network error');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            data-testid="admin-passcode-manager"
            className="rounded-[22px] border border-[var(--line)] bg-[var(--surface)] overflow-hidden"
        >
            <button
                type="button"
                onClick={onToggle}
                data-testid="admin-passcode-manager-toggle"
                data-cursor="hover"
                aria-expanded={open}
                className="w-full flex items-center justify-between gap-4 px-5 md:px-7 py-4 md:py-5 text-left hover:bg-[var(--surface-2)] transition-colors"
            >
                <div className="flex items-center gap-3">
                    <span className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-[var(--line-2)] text-[var(--accent)]">
                        <KeyRound size={14} />
                    </span>
                    <div>
                        <div className="font-mono-brand text-[10.5px] uppercase tracking-[0.22em] text-[var(--ink-3)]">
                            Security · Passcode
                        </div>
                        <div className="mt-0.5 font-display-italic text-lg md:text-xl text-[var(--ink)]">
                            {open ? 'Change admin passcode.' : 'Rotate the admin passcode.'}
                        </div>
                        {meta.updated_at && (
                            <div
                                data-testid="admin-passcode-last-rotated"
                                className="mt-1 font-mono-brand text-[10px] uppercase tracking-[0.18em] text-[var(--ink-3)] tabular-nums"
                            >
                                Last rotated · {formatRotatedDate(meta.updated_at)}
                            </div>
                        )}
                    </div>
                </div>
                <ChevronDown
                    size={16}
                    className={`text-[var(--ink-3)] transition-transform duration-300 ${
                        open ? 'rotate-180 text-[var(--accent)]' : ''
                    }`}
                />
            </button>

            <AnimatePresence initial={false}>
                {open && (
                    <motion.form
                        key="pm-form"
                        onSubmit={onSubmit}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.2, 0.8, 0.2, 1] }}
                        className="border-t border-[var(--line)]"
                    >
                        <div className="p-5 md:p-7 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                            {/* Current */}
                            <div className="md:col-span-2">
                                <label className="font-mono-brand text-[10.5px] uppercase tracking-[0.22em] text-[var(--ink-3)]">
                                    Current passcode
                                </label>
                                <div className="mt-1.5 relative">
                                    <input
                                        type={showCurrent ? 'text' : 'password'}
                                        value={current}
                                        onChange={(e) => setCurrent(e.target.value)}
                                        autoComplete="current-password"
                                        required
                                        data-testid="admin-passcode-current-input"
                                        className="w-full rounded-lg border border-[var(--line-2)] bg-[var(--surface-2)] px-4 py-2.5 pr-11 font-mono-brand text-[13px] text-[var(--ink)] focus:outline-none focus:border-[var(--accent)] transition-colors"
                                        placeholder="Your current passcode"
                                    />
                                    <button
                                        type="button"
                                        aria-label={showCurrent ? 'Hide' : 'Show'}
                                        onClick={() => setShowCurrent((v) => !v)}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-[var(--ink-3)] hover:text-[var(--ink)] transition-colors"
                                    >
                                        {showCurrent ? <EyeOff size={14} /> : <Eye size={14} />}
                                    </button>
                                </div>
                            </div>

                            {/* New */}
                            <div>
                                <label className="font-mono-brand text-[10.5px] uppercase tracking-[0.22em] text-[var(--ink-3)]">
                                    New passcode
                                </label>
                                <div className="mt-1.5 relative">
                                    <input
                                        type={showNext ? 'text' : 'password'}
                                        value={next}
                                        onChange={(e) => setNext(e.target.value)}
                                        minLength={MIN_PASSCODE_LEN}
                                        autoComplete="new-password"
                                        required
                                        data-testid="admin-passcode-next-input"
                                        className="w-full rounded-lg border border-[var(--line-2)] bg-[var(--surface-2)] px-4 py-2.5 pr-11 font-mono-brand text-[13px] text-[var(--ink)] focus:outline-none focus:border-[var(--accent)] transition-colors"
                                        placeholder={`At least ${MIN_PASSCODE_LEN} characters`}
                                    />
                                    <button
                                        type="button"
                                        aria-label={showNext ? 'Hide' : 'Show'}
                                        onClick={() => setShowNext((v) => !v)}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-[var(--ink-3)] hover:text-[var(--ink)] transition-colors"
                                    >
                                        {showNext ? <EyeOff size={14} /> : <Eye size={14} />}
                                    </button>
                                </div>
                                <div className="mt-1.5 font-mono-brand text-[10px] uppercase tracking-[0.16em] text-[var(--ink-3)] tabular-nums">
                                    {next.length}/{MIN_PASSCODE_LEN} min
                                </div>
                            </div>

                            {/* Confirm */}
                            <div>
                                <label className="font-mono-brand text-[10.5px] uppercase tracking-[0.22em] text-[var(--ink-3)]">
                                    Confirm new passcode
                                </label>
                                <input
                                    type={showNext ? 'text' : 'password'}
                                    value={confirm}
                                    onChange={(e) => setConfirm(e.target.value)}
                                    autoComplete="new-password"
                                    required
                                    data-testid="admin-passcode-confirm-input"
                                    className="mt-1.5 w-full rounded-lg border border-[var(--line-2)] bg-[var(--surface-2)] px-4 py-2.5 font-mono-brand text-[13px] text-[var(--ink)] focus:outline-none focus:border-[var(--accent)] transition-colors"
                                    placeholder="Retype the new passcode"
                                />
                                <div className="mt-1.5 font-mono-brand text-[10px] uppercase tracking-[0.16em] tabular-nums">
                                    {confirm.length === 0 ? (
                                        <span className="text-[var(--ink-3)]">
                                            &nbsp;
                                        </span>
                                    ) : confirm === next ? (
                                        <span className="text-[var(--accent)]">
                                            Matches
                                        </span>
                                    ) : (
                                        <span className="text-[var(--warn)]">
                                            No match
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Feedback + submit */}
                            <div className="md:col-span-2 flex items-center justify-between gap-4 flex-wrap">
                                <div className="min-h-[20px] font-mono-brand text-[11px] uppercase tracking-[0.16em]">
                                    {err && (
                                        <span
                                            data-testid="admin-passcode-error"
                                            className="text-[var(--warn)]"
                                        >
                                            {err}
                                        </span>
                                    )}
                                    {ok && (
                                        <span
                                            data-testid="admin-passcode-success"
                                            className="text-[var(--accent)] inline-flex items-center gap-1.5"
                                        >
                                            <Check size={12} />
                                            Passcode updated, you&apos;re still signed in.
                                        </span>
                                    )}
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={onToggle}
                                        data-cursor="hover"
                                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[var(--line-2)] text-[12px] font-medium text-[var(--ink-2)] hover:text-[var(--ink)] transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={busy || !current || !next || !confirm}
                                        data-testid="admin-passcode-submit"
                                        data-cursor="hover"
                                        className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-[var(--accent)] text-[var(--accent-ink)] text-[13px] font-semibold disabled:opacity-50 hover:opacity-90 transition-opacity"
                                    >
                                        {busy ? (
                                            <>
                                                <Loader2 size={13} className="animate-spin" />
                                                Updating…
                                            </>
                                        ) : (
                                            <>
                                                <KeyRound size={13} />
                                                Update passcode
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </motion.form>
                )}
            </AnimatePresence>
        </div>
    );
};

// ────────────────────────────────────────────────────────────
// Main admin dashboard
// ────────────────────────────────────────────────────────────
const Dashboard = ({ passcode, onLock, onPasscodeRotated }) => {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [query, setQuery] = useState('');
    const [activeSection, setActiveSection] = useState('stories');
    const [mediaType, setMediaType] = useState('all');
    // Sequence guard: React 18 StrictMode double-invokes useEffect and the
    // second GET can outlast an optimistic setFiles call from the resume
    // uploader, wiping the newly-flipped `is_active_resume` flag. We stamp
    // every load() with a monotonically increasing id and drop responses
    // whose id is older than the last one we already applied.
    const loadSeqRef = useRef(0);
    const lastAppliedSeqRef = useRef(0);

    const load = async () => {
        const seq = ++loadSeqRef.current;
        setError('');
        try {
            const r = await fetch(`${API}/admin/files`, {
                headers: { 'X-Admin-Passcode': passcode },
            });
            if (r.status === 401) {
                onLock();
                return;
            }
            if (!r.ok) throw new Error(`Load failed (${r.status})`);
            const data = await r.json();
            // Drop stale/out-of-order responses.
            if (seq < lastAppliedSeqRef.current) return;
            lastAppliedSeqRef.current = seq;
            setFiles(data);
        } catch (e) {
            if (seq < lastAppliedSeqRef.current) return;
            setError(e.message || 'Failed to load files');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const onDelete = async (id) => {
        const r = await fetch(`${API}/admin/files/${id}`, {
            method: 'DELETE',
            headers: { 'X-Admin-Passcode': passcode },
        });
        if (!r.ok) {
            setError(`Delete failed (${r.status})`);
            return;
        }
        setFiles((f) => f.filter((x) => x.id !== id));
    };

    const onToggleResume = async (id) => {
        const r = await fetch(`${API}/admin/files/${id}/set-resume`, {
            method: 'POST',
            headers: { 'X-Admin-Passcode': passcode },
        });
        if (!r.ok) {
            setError(`Could not update resume flag (${r.status})`);
            return;
        }
        const { is_active_resume } = await r.json();
        // Optimistic update: only one file at a time can be active.
        setFiles((list) =>
            list.map((f) => ({
                ...f,
                is_active_resume:
                    f.id === id ? is_active_resume : is_active_resume ? false : f.is_active_resume,
            }))
        );
    };

    const onAttach = async (id, slug) => {
        const r = await fetch(`${API}/admin/files/${id}/attach`, {
            method: 'POST',
            headers: {
                'X-Admin-Passcode': passcode,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ slug }),
        });
        if (!r.ok) {
            setError(`Attach failed (${r.status})`);
            return;
        }
        setFiles((list) =>
            list.map((f) =>
                f.id === id ? { ...f, case_study_slug: slug || null } : f
            )
        );
    };

    const onUploaded = (rec) => {
        // Dedupe by id, a late GET /admin/files response can also return the
        // record and we don't want React to warn about duplicate keys.
        setFiles((f) => [rec, ...f.filter((x) => x.id !== rec.id)]);
    };

    const activeResume = files.some((f) => f.is_active_resume);
    const typeOf = (ct) =>
        ct?.startsWith('image/')
            ? 'image'
            : ct === 'application/pdf'
                ? 'pdf'
                : ct?.startsWith('video/')
                    ? 'video'
                    : 'other';
    const typeCounts = files.reduce(
        (acc, f) => {
            acc.all += 1;
            acc[typeOf(f.content_type)] += 1;
            return acc;
        },
        { all: 0, image: 0, pdf: 0, video: 0, other: 0 }
    );

    const sections = [
        { key: 'stories', label: 'Stories', icon: Newspaper },
        { key: 'media', label: 'Media', icon: ImageIcon, hint: String(files.length) },
        { key: 'case-studies', label: 'Case studies', icon: LayoutGrid },
        { key: 'resume', label: 'Resume', icon: FileText, hint: activeResume ? '1' : '0' },
        { key: 'security', label: 'Security', icon: KeyRound },
    ];

    const mediaTabs = [
        { key: 'all', label: 'All', count: typeCounts.all },
        { key: 'image', label: 'Images', count: typeCounts.image },
        { key: 'pdf', label: 'PDFs', count: typeCounts.pdf },
        { key: 'video', label: 'Video', count: typeCounts.video },
        { key: 'other', label: 'Other', count: typeCounts.other },
    ].filter((t) => t.key === 'all' || t.count > 0);

    const q = query.trim().toLowerCase();
    const shownFiles = files.filter((f) => {
        const okType = mediaType === 'all' || typeOf(f.content_type) === mediaType;
        const okQ = !q || (f.original_filename || '').toLowerCase().includes(q);
        return okType && okQ;
    });

    const lockBtn =
        'inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full border border-[var(--line-2)] text-[12px] font-medium text-[var(--ink-2)] hover:text-[var(--ink)] hover:border-[var(--accent)] transition-colors';

    return (
        <section
            id="admin"
            data-testid="admin-dashboard"
            className="relative px-4 md:px-6 pt-24 md:pt-28 pb-16 md:pb-20"
        >
            <div className="mx-auto max-w-[1400px]">
                {/* Error banner, non-blocking */}
                <AnimatePresence>
                    {error && (
                        <motion.div
                            key="admin-err"
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.25 }}
                            data-testid="admin-error"
                            className="mb-4 flex items-center justify-between gap-4 px-5 py-3 rounded-full border border-[var(--warn)] bg-[color-mix(in_oklab,var(--warn)_8%,var(--surface))] text-[var(--warn)] font-mono-brand text-[12px] uppercase tracking-[0.18em]"
                        >
                            <span>{error}</span>
                            <button
                                type="button"
                                onClick={() => setError('')}
                                data-testid="admin-error-dismiss"
                                data-cursor="hover"
                                aria-label="Dismiss error"
                                className="inline-flex items-center justify-center w-6 h-6 rounded-full border border-[var(--warn)] text-[var(--warn)] hover:opacity-70 transition-opacity"
                            >
                                ×
                            </button>
                        </motion.div>
                    )}
                </AnimatePresence>

                <div className="grid grid-cols-12 gap-5 md:gap-6">
                    {/* Sidebar navigation */}
                    <aside className="col-span-12 lg:col-span-3">
                        <div className="lg:sticky lg:top-24 rounded-[22px] border border-[var(--line)] bg-[var(--surface)] p-4 md:p-5">
                            <div className="hidden lg:block px-1">
                                <div className="inline-flex items-center gap-2 font-mono-brand text-[10px] uppercase tracking-[0.24em] text-[var(--ink-3)]">
                                    <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent)] pulse-dot" />
                                    CMS · Portfolio
                                </div>
                                <div className="mt-2 font-display leading-none text-3xl text-[var(--ink)]">
                                    Admin<span className="text-[var(--accent)]">.</span>
                                </div>
                            </div>

                            <nav className="lg:mt-5 flex flex-wrap lg:flex-col gap-1.5">
                                {sections.map((s) => {
                                    const on = activeSection === s.key;
                                    const Icon = s.icon;
                                    return (
                                        <button
                                            key={s.key}
                                            type="button"
                                            onClick={() => setActiveSection(s.key)}
                                            data-testid={`admin-nav-${s.key}`}
                                            aria-current={on}
                                            data-cursor="hover"
                                            className={`shrink-0 lg:w-full inline-flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-left transition-colors ${
                                                on
                                                    ? 'bg-[var(--accent)] text-[var(--accent-ink)]'
                                                    : 'text-[var(--ink-2)] hover:text-[var(--ink)] hover:bg-[var(--surface-2)]'
                                            }`}
                                        >
                                            <Icon size={15} />
                                            <span className="font-sans-brand text-[13.5px] font-medium whitespace-nowrap">
                                                {s.label}
                                            </span>
                                            {s.hint && (
                                                <span
                                                    className={`ml-auto pl-2 font-mono-brand text-[10px] tabular-nums ${
                                                        on
                                                            ? 'text-[var(--accent-ink)]/70'
                                                            : 'text-[var(--ink-3)]'
                                                    }`}
                                                >
                                                    {s.hint}
                                                </span>
                                            )}
                                        </button>
                                    );
                                })}
                            </nav>

                            <div className="hidden lg:flex items-center gap-2 mt-5 pt-4 border-t border-[var(--line)]">
                                <button
                                    type="button"
                                    onClick={onLock}
                                    data-testid="admin-lock-button"
                                    data-cursor="hover"
                                    className={`flex-1 ${lockBtn}`}
                                >
                                    <Lock size={11} /> Lock
                                </button>
                                <Link
                                    to="/"
                                    data-cursor="hover"
                                    title="Back home"
                                    className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-[var(--line-2)] text-[var(--ink-2)] hover:text-[var(--ink)] hover:border-[var(--accent)] transition-colors"
                                >
                                    <Home size={13} />
                                </Link>
                            </div>
                        </div>

                        {/* Mobile lock */}
                        <div className="lg:hidden mt-3">
                            <button
                                type="button"
                                onClick={onLock}
                                data-testid="admin-lock-button-mobile"
                                data-cursor="hover"
                                className={`w-full ${lockBtn}`}
                            >
                                <Lock size={11} /> Lock
                            </button>
                        </div>
                    </aside>

                    {/* Main content */}
                    <main className="col-span-12 lg:col-span-9 flex flex-col gap-5">
                        {activeSection === 'stories' && (
                            <StoriesManager passcode={passcode} />
                        )}

                        {activeSection === 'media' && (
                            <div
                                data-testid="admin-media-library"
                                className="rounded-[22px] border border-[var(--line)] bg-[var(--surface)] p-5 md:p-7"
                            >
                                <div className="flex items-end justify-between gap-4 flex-wrap">
                                    <div>
                                        <div className="font-mono-brand text-[10.5px] uppercase tracking-[0.22em] text-[var(--ink-3)]">
                                            Media library
                                        </div>
                                        <h2 className="mt-1 font-display leading-none text-2xl md:text-3xl text-[var(--ink)]">
                                            Files{' '}
                                            <span className="font-display-italic text-[var(--accent)]">
                                                &amp; assets
                                            </span>
                                            <span className="text-[var(--ink-2)]">.</span>
                                        </h2>
                                    </div>
                                    {files.length > 0 && (
                                        <input
                                            value={query}
                                            onChange={(e) => setQuery(e.target.value)}
                                            placeholder="Search files…"
                                            data-testid="admin-media-search"
                                            className="w-full sm:w-64 rounded-full border border-[var(--line-2)] bg-[var(--surface-2)] px-4 py-2 font-sans-brand text-[13px] text-[var(--ink)] placeholder:text-[var(--ink-3)] focus:outline-none focus:border-[var(--accent)] transition-colors"
                                        />
                                    )}
                                </div>

                                <div className="mt-5">
                                    <Uploader
                                        passcode={passcode}
                                        onUploaded={onUploaded}
                                    />
                                </div>

                                {files.length > 0 && (
                                    <div className="mt-5 flex items-center gap-2 flex-wrap">
                                        {mediaTabs.map((t) => (
                                            <button
                                                key={t.key}
                                                type="button"
                                                onClick={() => setMediaType(t.key)}
                                                data-testid={`admin-media-tab-${t.key}`}
                                                data-cursor="hover"
                                                className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border text-[12px] font-medium transition-colors ${
                                                    mediaType === t.key
                                                        ? 'border-[var(--accent)] bg-[color-mix(in_oklab,var(--accent)_10%,transparent)] text-[var(--accent)]'
                                                        : 'border-[var(--line-2)] text-[var(--ink-2)] hover:text-[var(--ink)]'
                                                }`}
                                            >
                                                {t.label}
                                                <span className="font-mono-brand text-[10px] tabular-nums opacity-70">
                                                    {t.count}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                )}

                                <div className="mt-5">
                                    {loading ? (
                                        <div className="py-12 flex items-center justify-center text-[var(--ink-3)]">
                                            <Loader2 size={22} className="animate-spin" />
                                        </div>
                                    ) : files.length === 0 ? (
                                        <div className="py-10 rounded-[18px] border border-dashed border-[var(--line)] bg-[var(--surface-2)] text-center">
                                            <div className="font-display-italic text-2xl text-[var(--ink-2)]">
                                                No files yet.
                                            </div>
                                            <div className="mt-2 font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--ink-3)]">
                                                Drop something above to get started
                                            </div>
                                        </div>
                                    ) : shownFiles.length === 0 ? (
                                        <div className="py-10 text-center font-mono-brand text-[10.5px] uppercase tracking-[0.22em] text-[var(--ink-3)]">
                                            No files match this filter
                                        </div>
                                    ) : (
                                        <div
                                            data-testid="admin-files-grid"
                                            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-2.5 md:gap-3"
                                        >
                                            <AnimatePresence mode="popLayout">
                                                {shownFiles.map((f) => (
                                                    <FileCard
                                                        key={f.id}
                                                        file={f}
                                                        onDelete={onDelete}
                                                        onToggleResume={onToggleResume}
                                                        onAttach={onAttach}
                                                    />
                                                ))}
                                            </AnimatePresence>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {activeSection === 'case-studies' && (
                            <CaseStudyManager passcode={passcode} />
                        )}

                        {activeSection === 'resume' && (
                            <ResumeManager
                                passcode={passcode}
                                files={files}
                                loading={loading}
                                onReplaced={(rec) => {
                                    setFiles((list) => {
                                        const withoutSelf = list.filter(
                                            (f) => f.id !== rec.id
                                        );
                                        return [
                                            rec,
                                            ...withoutSelf.map((f) => ({
                                                ...f,
                                                is_active_resume: false,
                                            })),
                                        ];
                                    });
                                }}
                                onReload={load}
                            />
                        )}

                        {activeSection === 'security' && (
                            <PasscodeManager
                                passcode={passcode}
                                onRotated={onPasscodeRotated}
                                defaultOpen
                            />
                        )}
                    </main>
                </div>
            </div>
        </section>
    );
};

// ────────────────────────────────────────────────────────────
// Route entry
// ────────────────────────────────────────────────────────────
const Admin = () => {
    const [passcode, setPasscode] = useState(() =>
        localStorage.getItem(LS_KEY)
    );

    // Silently re-verify saved passcode on mount
    useEffect(() => {
        const saved = localStorage.getItem(LS_KEY);
        if (!saved) return;
        (async () => {
            try {
                const r = await fetch(`${API}/admin/verify`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ passcode: saved }),
                });
                if (!r.ok) {
                    localStorage.removeItem(LS_KEY);
                    setPasscode(null);
                }
            } catch {
                /* keep passcode; user can retry */
            }
        })();
    }, []);

    if (!passcode) return <Gate onUnlock={setPasscode} />;

    return (
        <Dashboard
            passcode={passcode}
            onPasscodeRotated={(newCode) => setPasscode(newCode)}
            onLock={() => {
                localStorage.removeItem(LS_KEY);
                setPasscode(null);
            }}
        />
    );
};

export default Admin;
